import { FC } from "react";
import CustomModal from "../../common/custom-modal/custom-modal";
import { Colors } from "../../../configs";
import { PaymentTypeEnum } from "../../../typings/enum/paymentTypes";

export interface IPaymentErrorModalProps {
    open: boolean;
    onClose: () => void;
    paymentType?: PaymentTypeEnum;
    billAmount?: number;
    errorMessage?: string;
    tenderedAmount?: number;
    checkNumber?: string;
    outstandingAmount?: number;
    availableCredit?: number;
    customerName?: string;
    customerId?: number;
    onRetry?: () => void;
}

const ERROR_RED = "#930000";

function ErrorIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[2rem] w-[2rem]" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
            <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
    );
}

function RetryIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[1.125rem] w-[1.125rem]" fill="none" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M20 4v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ReceiptIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[1.125rem] w-[1.125rem]" fill="none" aria-hidden="true">
            <path d="M7 4h10v16l-2-1.5L13 20l-2-1.5L9 20l-2-1.5L7 20V4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M9.5 9h5M9.5 12h5M9.5 15h3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD" });

interface ErrorConfig {
    modalTitle: string;
    errorStatus: string;
    methodLabel: string;
    methodSubtitle: string;
    icon: string;
    accent: string;
    defaultReason: string;
    recommendation: string;
    primaryActionLabel: string;
    secondaryActionLabel: string;
}

function getErrorConfig(
    paymentType: PaymentTypeEnum | undefined,
    checkNumber?: string,
    customerName?: string,
    customerId?: number,
): ErrorConfig {
    switch (paymentType) {
        case PaymentTypeEnum.CASH:
            return {
                modalTitle: "Payment Not Completed",
                errorStatus: "Cash Entry Error",
                methodLabel: "Cash Payment",
                methodSubtitle: "Entered amount is less than the bill total",
                icon: "💵", accent: "#7ABF7A",
                defaultReason: "The cash amount entered does not cover the full bill.",
                recommendation: "Review the entry and collect the remaining balance before completing the sale.",
                primaryActionLabel: "Correct Amount",
                secondaryActionLabel: "Mark Pay Later",
            };
        case PaymentTypeEnum.CARD:
            return {
                modalTitle: "Payment Not Completed",
                errorStatus: "Card Declined",
                methodLabel: "Card Payment",
                methodSubtitle: "Transaction was declined by the card issuer",
                icon: "💳", accent: "#7EADEB",
                defaultReason: "The card issuer declined the transaction.",
                recommendation: "Ask the customer to retry, use another card, or choose a different payment method.",
                primaryActionLabel: "Retry Card",
                secondaryActionLabel: "Use Another Method",
            };
        case PaymentTypeEnum.CHECK:
            return {
                modalTitle: "Payment Not Completed",
                errorStatus: "Check Not Accepted",
                methodLabel: "Check Payment",
                methodSubtitle: checkNumber ? `Check #${checkNumber}` : "Check payment",
                icon: "🧾", accent: "#B596E2",
                defaultReason: "The check could not be verified for acceptance.",
                recommendation: "Request another form of payment or ask the customer to contact their bank.",
                primaryActionLabel: "Recheck",
                secondaryActionLabel: "Another Method",
            };
        case PaymentTypeEnum.MONTHLY:
            return {
                modalTitle: "Transaction Error",
                errorStatus: "Monthly Account Not Approved",
                methodLabel: "Monthly Account",
                methodSubtitle: customerName
                    ? `${customerName}${customerId ? ` • Account #${customerId}` : ""}`
                    : "Monthly account payment",
                icon: "👤", accent: "#7BCFD0",
                defaultReason: "This charge account is not eligible for additional posting at this time.",
                recommendation: "Collect payment now or ask an authorized manager to review the account.",
                primaryActionLabel: "Manager Override",
                secondaryActionLabel: "Take Payment Now",
            };
        case PaymentTypeEnum.LATER:
            return {
                modalTitle: "Transaction Error",
                errorStatus: "Pay Later Not Allowed",
                methodLabel: "Pay Later",
                methodSubtitle: "Deferred payment not permitted",
                icon: "📅", accent: "#F0B15B",
                defaultReason: "This ticket does not meet the conditions required to defer payment.",
                recommendation: "Take payment now or request manager approval before releasing the order.",
                primaryActionLabel: "Request Approval",
                secondaryActionLabel: "Take Payment",
            };
        case PaymentTypeEnum.STORE_CREDIT:
            return {
                modalTitle: "Transaction Error",
                errorStatus: "Store Credit Not Applied",
                methodLabel: "Store Credit",
                methodSubtitle: customerId ? `Store Credit #SC-${customerId}` : "Store credit",
                icon: "🎁", accent: "#F29AAD",
                defaultReason: "Available store credit is not enough to cover this transaction.",
                recommendation: "Apply the remaining balance using cash, card, check, or monthly account if allowed.",
                primaryActionLabel: "Apply Partial Credit",
                secondaryActionLabel: "Split Payment",
            };
        default:
            return {
                modalTitle: "Transaction Error",
                errorStatus: "Payment Failed",
                methodLabel: "Payment",
                methodSubtitle: "An error occurred during processing",
                icon: "💰", accent: "#7ABF7A",
                defaultReason: "An unexpected error occurred. Please try again.",
                recommendation: "Please try again or use a different payment method.",
                primaryActionLabel: "Try Again",
                secondaryActionLabel: "Change Method",
            };
    }
}

type DetailRowData = { left: string; right: string; divider?: boolean; strong?: boolean };

function getErrorRows(props: IPaymentErrorModalProps): DetailRowData[] {
    const { paymentType, billAmount, tenderedAmount, checkNumber, outstandingAmount, availableCredit } = props;
    const bill = billAmount ?? 0;

    switch (paymentType) {
        case PaymentTypeEnum.CASH: {
            const tendered = tenderedAmount ?? 0;
            return [
                { left: "Bill Total",        right: fmt(bill),                        strong: true },
                { left: "Cash Entered",      right: fmt(tendered),                    strong: true },
                { left: "Remaining Balance", right: fmt(Math.max(0, bill - tendered)), divider: true, strong: true },
            ];
        }
        case PaymentTypeEnum.CARD:
            return [
                { left: "Bill Total",        right: fmt(bill), strong: true },
                { left: "Attempted Charge",  right: fmt(bill), strong: true },
            ];
        case PaymentTypeEnum.CHECK:
            return [
                { left: "Bill Total",    right: fmt(bill),          strong: true },
                { left: "Check Amount",  right: fmt(bill),          strong: true },
                { left: "Check #",       right: checkNumber ?? "—", divider: true },
                { left: "Verification",  right: "Failed" },
            ];
        case PaymentTypeEnum.MONTHLY: {
            const prev = outstandingAmount ?? 0;
            return [
                { left: "Previous Balance",     right: fmt(prev),        strong: true },
                { left: "Current Charges",      right: fmt(bill),        strong: true },
                { left: "Requested New Balance", right: fmt(prev + bill), divider: true, strong: true },
                { left: "Account Status",       right: "Credit Hold" },
            ];
        }
        case PaymentTypeEnum.LATER:
            return [
                { left: "Bill Total",        right: fmt(bill),      strong: true },
                { left: "Policy Check",      right: "Not Eligible", divider: true },
                { left: "Approval Required", right: "Manager" },
            ];
        case PaymentTypeEnum.STORE_CREDIT: {
            const credit = availableCredit ?? 0;
            return [
                { left: "Available Credit", right: fmt(credit),                    strong: true },
                { left: "Bill Amount",      right: fmt(bill),                      strong: true },
                { left: "Short Balance",    right: fmt(Math.max(0, bill - credit)), divider: true, strong: true },
                { left: "Credit Status",    right: "Insufficient" },
            ];
        }
        default:
            return billAmount !== undefined
                ? [{ left: "Bill Total", right: fmt(bill), strong: true }]
                : [];
    }
}

const PaymentErrorModal: FC<IPaymentErrorModalProps> = (props) => {
    const { open, onClose, paymentType, errorMessage, customerName, customerId, checkNumber, onRetry } = props;

    const config = getErrorConfig(paymentType, checkNumber, customerName, customerId);
    const rows = getErrorRows(props);
    const displayReason = errorMessage || config.defaultReason;

    const handleClose = () => onClose();
    const handleRetry = () => { onRetry?.(); onClose(); };

    return (
        <CustomModal
            title={config.modalTitle}
            openModal={open}
            onClose={handleClose}
            width="38rem"
            height="auto"
        >
            <div className="flex flex-col pb-[0.25rem] pt-[0.25rem]" style={{ minHeight: "34rem" }}>

                {/* Error icon */}
                <div className="mb-[0.75rem] mt-[0.25rem] flex justify-center">
                    <div
                        className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: ERROR_RED }}
                    >
                        <ErrorIcon />
                    </div>
                </div>

                {/* Error status */}
                <div className="text-center text-[1rem] font-bold" style={{ color: ERROR_RED }}>
                    {config.errorStatus}
                </div>

                {/* Recommendation */}
                <div
                    className="mb-[1rem] mt-[0.25rem] text-center text-[0.75rem] font-medium"
                    style={{ color: "rgba(81,81,81,0.78)" }}
                >
                    {config.recommendation}
                </div>

                {/* Payment method section */}
                <div className="mb-[1rem] flex items-center gap-[0.75rem]">
                    <div
                        className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full text-[1.625rem]"
                        style={{ backgroundColor: `${config.accent}33` }}
                    >
                        {config.icon}
                    </div>
                    <div>
                        <div className="text-[0.875rem] font-bold leading-tight" style={{ color: "#515151" }}>
                            {config.methodLabel}
                        </div>
                        <div className="mt-[0.125rem] text-[0.75rem]" style={{ color: "rgba(81,81,81,0.75)" }}>
                            {config.methodSubtitle}
                        </div>
                    </div>
                </div>

                {/* Detail panel */}
                {rows.length > 0 && (
                    <div
                        className="mb-[1rem] rounded-[0.75rem] p-[1rem]"
                        style={{ backgroundColor: "#F4F5F6" }}
                    >
                        {rows.map((row, idx) => (
                            <div
                                key={idx}
                                className={`flex items-center justify-between gap-4 py-[0.375rem] text-[0.8125rem] ${
                                    row.divider ? "mt-[0.25rem] border-t pt-[0.75rem]" : ""
                                }`}
                                style={row.divider ? { borderColor: "#D8DDE1" } : undefined}
                            >
                                <span style={{ color: "#515151" }}>{row.left}</span>
                                <span
                                    className={row.strong ? "font-bold" : "font-semibold"}
                                    style={{ color: "#515151" }}
                                >
                                    {row.right}
                                </span>
                            </div>
                        ))}

                        {/* Error reason box */}
                        <div
                            className="mt-[0.75rem] rounded-[0.625rem] border p-[0.75rem]"
                            style={{ backgroundColor: "#FFF4F4", borderColor: "#F0CACA" }}
                        >
                            <div
                                className="text-[0.6875rem] font-bold uppercase tracking-[0.12em]"
                                style={{ color: ERROR_RED }}
                            >
                                Reason
                            </div>
                            <div
                                className="mt-[0.25rem] text-[0.875rem] font-semibold leading-snug"
                                style={{ color: "#515151" }}
                            >
                                {displayReason}
                            </div>
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                <div className="mb-[0.75rem] grid grid-cols-2 gap-[0.5rem]">
                    <button
                        type="button"
                        onClick={handleRetry}
                        className="flex h-[3rem] items-center justify-center gap-[0.5rem] rounded-[0.75rem] border text-[0.8125rem] font-bold transition hover:-translate-y-[0.0625rem]"
                        style={{
                            backgroundColor: ERROR_RED,
                            borderColor: ERROR_RED,
                            color: "white",
                            boxShadow: "0 0.5rem 1.125rem rgba(147,0,0,0.18)",
                        }}
                    >
                        <RetryIcon />
                        <span>{config.primaryActionLabel}</span>
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="flex h-[3rem] items-center justify-center gap-[0.5rem] rounded-[0.75rem] border text-[0.8125rem] font-bold transition hover:-translate-y-[0.0625rem]"
                        style={{
                            backgroundColor: Colors.SECONDARY,
                            borderColor: Colors.SECONDARY,
                            color: "white",
                            boxShadow: "0 0.5rem 1.125rem rgba(0,121,194,0.18)",
                        }}
                    >
                        <ReceiptIcon />
                        <span>{config.secondaryActionLabel}</span>
                    </button>
                </div>

                {/* Done — mt-auto pins it to the bottom */}
                <button
                    type="button"
                    onClick={handleClose}
                    className="mt-auto h-[2.75rem] w-full rounded-[0.5625rem] border text-[1rem] font-bold transition hover:bg-[#F8F8F8]"
                    style={{ backgroundColor: "white", color: "#515151", borderColor: "#D9D9D9" }}
                >
                    Done
                </button>
            </div>
        </CustomModal>
    );
};

export default PaymentErrorModal;
